import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Sprout, Hammer, Users, Globe, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HomePage = () => {
 return (
 <div className="homepage">
 {/* Hero Section */}
 <section className="hero-section">
 <div className="hero-content">
 <div className="hero-badge">🇰🇪 Opening September 2026 | Runda, Nairobi, Kenya</div>
 <h1 className="hero-title">
 Reimagining Education from the Ground Up
 </h1>
 <p className="hero-subtitle">
 Nova Roots is creating a new kind of African school: one that grows from the soil of Africa and prepares children to thrive in a changing world. Blending technology, trade, agriculture and leadership into an education model that is both deeply human and future-ready.
 </p>
 <div className="hero-stats">
 <div className="stat-item">
 <span className="stat-number">60%+</span>
 <span className="stat-label">Youth unemployment in Africa</span>
 </div>
 <div className="stat-divider"></div>
 <div className="stat-item">
 <span className="stat-number">40%</span>
 <span className="stat-label">World's youth in Africa by 2050</span>
 </div>
 </div>
 <p className="hero-urgency">
 The system is failing to prepare children for real life. Nova Roots exists to restore dignity, creativity, and purpose to learning.
 </p>
 <div className="hero-cta">
 <Link to="/contact">
 <Button className="cta-button cta-large">
 Register Interest for 2026 <ArrowRight className="ml-2" size={20} />
 </Button>
 </Link>
 <Link to="/nova-roots-way">
 <Button variant="outline" className="secondary-button">
 Explore Our Way
 </Button>
 </Link>
 </div>
 </div>
 <div className="hero-image">
 <img 
 src="/ai-learning-hero.jpg" 
 alt="AI-powered personalised learning at Nova Roots"
 />
 <div className="image-caption">AI-powered personalised learning tailored to each child</div>
 </div>
 </section>

 {/* Vision Section */}
 <section className="vision-section african-accent-border">
 <div className="section-container">
 <h2 className="vision-title">Our Vision</h2>
 <p className="vision-statement">
 To reimagine education in Africa so every child grows with roots of dignity and wings of opportunity.
 </p>
 <div className="vision-grid">
 <div className="vision-card">
 <h3>Blend African heritage with innovation and sustainability</h3>
 </div>
 <div className="vision-card">
 <h3>Create environments where curiosity, trade, agriculture and tech meet</h3>
 </div>
 <div className="vision-card">
 <h3>Build children who lead with skill, purpose and humanity</h3>
 </div>
 </div>
 </div>
 </section>

 {/* Proof Section */}
 <section className="proof-section">
 <div className="section-container">
 <p className="proof-text">
 Children progress through clear academic mastery at their own pace, supported by trained Walezi, personalised learning systems and daily application in real life.
 </p>
 <div className="proof-points">
 <div className="proof-item">
 <p>No ranking</p>
 </div>
 <div className="proof-divider"></div>
 <div className="proof-item">
 <p>No rote memorisation</p>
 </div>
 <div className="proof-divider"></div>
 <div className="proof-item">
 <p>No one left unseen</p>
 </div>
 </div>
 </div>
 </section>

 {/* Core Pillars */}
 <section className="pillars-section african-pattern-subtle">
 <div className="section-container">
 <h2 className="section-title">Three Forces, One System</h2>
 <p className="pillars-intro">
 An education model rooted in African soil, connected to the world.
 </p>
 <div className="pillars-grid">
 <Card className="pillar-card">
 <CardContent className="pillar-content">
 <div className="pillar-icon">
 <BookOpen size={32} />
 </div>
 <h3>Advanced Learning Technology</h3>
 <p>
 2-hour AI-powered personalised learning daily. Children move at their own pace through mastery-based academics. Technology supports insight and tracking—human judgement always leads.
 </p>
 </CardContent>
 </Card>

 <Card className="pillar-card">
 <CardContent className="pillar-content">
 <div className="pillar-icon">
 <Users size={32} />
 </div>
 <h3>Child-Centred Pedagogy</h3>
 <p>
 Mixed-age learning pods. Inquiry-led exploration. Head, heart, hands framework. Respect for rhythm, play, curiosity and agency. Whole child learning informed by proven approaches.
 </p>
 </CardContent>
 </Card>

 <Card className="pillar-card">
 <CardContent className="pillar-content">
 <div className="pillar-icon">
 <Globe size={32} />
 </div>
 <h3>Real-Life Learning</h3>
 <p>
 Farm-to-table agriculture. Trade and craft. Entrepreneurship and enterprise. Practical skills with student agency. Children learn how the world works, not just how school works.
 </p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 {/* Daily Structure */}
 <section className="structure-section">
 <div className="section-container">
 <div className="structure-content">
 <div className="structure-text">
 <h2 className="section-title">Rigour Without Burnout</h2>
 <div className="daily-breakdown">
 <div className="time-block morning-block">
 <h3>Morning (2 Hours)</h3>
 <p>AI-powered personalised academic mastery</p>
 <ul>
 <li>Literacy & numeracy foundations</li>
 <li>Individual pace, no rushing, no holding back</li>
 <li>Real-time progress tracking</li>
 </ul>
 </div>
 <div className="time-block afternoon-block">
 <h3>Remainder of Day</h3>
 <p>Real-world application & skill development</p>
 <ul>
 <li>Life skills and independence</li>
 <li>Financial literacy & enterprise</li>
 <li>Agriculture & sustainability</li>
 <li>Trade, craft & problem-solving</li>
 <li>Leadership & collaboration</li>
 </ul>
 </div>
 </div>
 <Link to="/learning-model">
 <Button variant="outline" className="learn-more-button">
 Explore Learning Model <ArrowRight className="ml-2" size={16} />
 </Button>
 </Link>
 </div>
 <div className="structure-image">
 <img 
 src="/children-activities.png" 
 alt="Children engaged in hands-on learning activities"
 />
 </div>
 </div>
 </div>
 </section>

 {/* Outcomes */}
 <section className="outcomes-section">
 <div className="section-container">
 <h2 className="section-title">Children Who Thrive</h2>
 <p className="outcomes-intro">
 Nova Roots intentionally develops children who are:
 </p>
 <div className="outcomes-grid">
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Critical thinkers</p>
 </div>
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Problem solvers</p>
 </div>
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Independent learners</p>
 </div>
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Builders and creators</p>
 </div>
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Confident navigating real life and complexity</p>
 </div>
 <div className="outcome-item">
 <div className="outcome-circle"></div>
 <p>Emotionally resilient and self-aware</p>
 </div>
 </div>
 </div>
 </section>

 {/* Scale & Impact */}
 <section className="impact-section">
 <div className="section-container">
 <h2 className="section-title">Built to Scale, Rooted in Purpose</h2>
 <p className="impact-intro">
 Nova Roots begins as a micro school—a living laboratory proving African-designed learning works.
 </p>
 <div className="impact-timeline">
 <div className="timeline-item">
 <div className="timeline-year">2026</div>
 <div className="timeline-content">
 <h3>Runda Pilot</h3>
 <p>30 learners, 8 Walezi, mixed-age pods</p>
 </div>
 </div>
 <div className="timeline-arrow">→</div>
 <div className="timeline-item">
 <div className="timeline-year">Year 5</div>
 <div className="timeline-content">
 <h3>500 Learners</h3>
 <p>Wider community reach through training</p>
 </div>
 </div>
 <div className="timeline-arrow">→</div>
 <div className="timeline-item">
 <div className="timeline-year">Year 10</div>
 <div className="timeline-content">
 <h3>5,000+ Enrolled</h3>
 <p>Tens of thousands impacted continent-wide</p>
 </div>
 </div>
 </div>
 <p className="impact-statement">
 Nova Roots aims to ignite a movement redefining African education, not just build schools.
 </p>
 </div>
 </section>

 {/* Founder Story */}
 <section className="founder-story-section">
 <div className="section-container">
 <div className="founder-story-content">
 <div className="founder-story-text">
 <h2>Why I Built Nova Roots</h2>
 <p className="founder-name">Taisha Kama Bailey, Founder</p>
 <p>
 Nova Roots was born from my heart as a mother and my conviction as an educator. After more than twenty years in classrooms across London and Nairobi, I saw how traditional systems measured children by test scores instead of their humanity. I watched bright, creative minds fade under the weight of conformity.
 </p>
 <p>
 When I relocated to Kenya, I realised Africa needed a new kind of school—one rooted in dignity, purpose, and relevance. Nova Roots became that vision: a place where every child is seen, valued, and prepared for life.
 </p>
 <p className="founder-closing">
 This is not a concept born in boardrooms; it is my life's work, shaped by teaching, motherhood, and faith in Africa's future. Nova Roots is more than a school. It is a movement to restore dignity to learning and give Africa's children roots to stand tall and wings to soar.
 </p>
 <Link to="/founder">
 <Button variant="outline" className="learn-more-button">
 Read Full Story <ArrowRight className="ml-2" size={16} />
 </Button>
 </Link>
 </div>
 <div className="founder-story-image">
 <img src="/children-reading.png" alt="Children learning at Nova Roots" />
 </div>
 </div>
 </div>
 </section>

 {/* CTA Section */}
 <section className="cta-section">
 <div className="section-container">
 <div className="cta-content">
 <h2>Ready to Be Part of This Movement?</h2>
 <p>
 Nova Roots opens September 2026. Limited places available for our founding cohort.
 </p>
 <div className="cta-buttons">
 <Link to="/contact">
 <Button className="cta-button cta-large">
 Register Your Interest
 </Button>
 </Link>
 <Link to="/nairobi-pilot">
 <Button variant="outline" className="secondary-button-light">
 Learn About the Pilot
 </Button>
 </Link>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
};

export default HomePage;
